import { useState, useEffect } from 'react';
import { getVersionTimeline } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { TRequestBackType } from '@op/utils/requestUtil';
import { useStableValue } from '@opm/utils/stableKey';
import { getVersionConfig } from '@op/pages/SteamPerformance/VersionAnalysis/config';

export type TMetricItem =
  | 'reviews'
  | 'pos_ratio'
  | 'pos_reviews'
  | 'neg_ratio'
  | 'neg_reviews';

/** series.name → { en, cn } 映射，由消费方按 isEn 决定显示哪个 */
export type TUpdateTitleMap = Record<string, { en: string; cn: string }>;

interface UseVersionTimelineOptions {
  filterParams: Record<string, any>;
  options?: { enabled?: boolean };
}

function formatToRequestBackType(
  response: any,
  filterParams?: Record<string, any>,
): {
  chartData: TRequestBackType;
  titleMap: TUpdateTitleMap;
} {
  const series = response?.series || [];
  const maxDay = response?.max_day || 0;

  if (!series.length || maxDay === 0) {
    return { chartData: { isEmpty: true }, titleMap: {} };
  }

  // 配置游戏：根据 group_by 字段确定 titleMap 的 key 和文案
  const isConfigGame = filterParams?.is_dune;
  const groupBy = filterParams?.group_by;

  // 获取版本配置（用于 uniqueKeyFields）
  const versionConfig = getVersionConfig(filterParams?.edition_unified_id);
  const uniqueKeyFields = versionConfig?.uniqueKeyFields;

  const titleMap: TUpdateTitleMap = {};
  const formattedSeries = series.map((item: any, index: number) => {
    let key: string;

    if (isConfigGame && groupBy) {
      if (uniqueKeyFields?.length) {
        // 配置游戏且有 uniqueKeyFields：组合多个字段作为唯一 key（如 season__chapter）
        key = uniqueKeyFields
          .map(field => item?.[field] ?? '')
          .filter(Boolean)
          .join('__');
        if (!key) key = `series-${index}`;
        // 展示文案优先使用 update_title（更可读），中英文一致
        const displayText = item?.update_title || key;

        titleMap[key] = { en: displayText, cn: displayText };
      } else {
        // 配置游戏：取 item[groupBy] 作为 key，中英文一致
        key = item?.[groupBy] || `series-${index}`;
        titleMap[key] = { en: key, cn: key };
      }
    } else {
      // 默认：用 update_title_en 作为稳定 key（不随语言切换变化），fallback 到 cn
      const en = item?.update_title_en || '';
      const cn = item?.update_title_cn || '';
      key = en || cn || `series-${index}`;
      titleMap[key] = { en, cn };
    }

    return {
      name: key,
      data: Array.from({ length: maxDay }, (_, i) => {
        const point = (item.data || []).find((p: any) => p.day === `D${i + 1}`);
        return point?.value || null;
      }),
    };
  });

  return {
    chartData: {
      series: formattedSeries,
      xAxis: Array.from({ length: maxDay }, (_, i) => `D${i + 1}`),
      legend: formattedSeries.map((s: { name: string }) => s.name),
      isEmpty: false,
    },
    titleMap,
  };
}

export const isPercentageMetric = (metric: TMetricItem): boolean =>
  metric === 'pos_ratio' || metric === 'neg_ratio';

export const useVersionTimeline = ({
  filterParams,
}: UseVersionTimelineOptions) => {
  const [state, setState] = useState<{
    chartData: TRequestBackType;
    titleMap: TUpdateTitleMap;
    maxDays: number;
  }>({
    chartData: { isEmpty: true },
    titleMap: {},
    maxDays: 0,
  });
  const filter = useStableValue(filterParams);

  const { wrapperCancelRequest, loading } = useCancelRequestHook<any>({
    clear: () => {
      setState({
        chartData: { isEmpty: true },
        titleMap: {},
        maxDays: 0,
      });
    },
  });

  const fetchTimeline = (params: Record<string, any>): void => {
    if (!params?.edition_unified_id) return;
    wrapperCancelRequest(getVersionTimeline, params)
      .then(res => {
        if (!res) return;
        const { chartData: formatted, titleMap: nextTitleMap } =
          formatToRequestBackType(res, params);

        setState({
          chartData: formatted,
          titleMap: nextTitleMap,
          maxDays: res?.max_day || 0,
        });
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchTimeline(filter);
  }, [filter]);

  return {
    ...state,
    loading,
  };
};
